// External libraries
import moment from 'moment';
import { List, fromJS, Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import memoize from 'transmute/memoize';
import camelCase from 'hs-lodash/camelCase';
import mapKeys from 'hs-lodash/mapKeys';
import pick from 'hs-lodash/pick';

// @ts-expect-error untyped
import SafeStorage from 'SafeStorage';
import PortalIdParser from 'PortalIdParser';
import { buildTypedMap } from '../types/TypedMap';
import { DEAL_STAGE, FORECAST_CATEGORY, DEAL_GROUPING_TYPE } from '../constants/DealGroupingType';
import { FILTERS_PROPERTY_NAME, FILTERS, TIME_PERIOD_FILTERS, TIME_PERIOD_FILTER_CUSTOM, TIME_PERIOD_FILTERS_TO_OPTION_TEXT_STRINGS, FILTER_STORAGE_KEY, NO_VALUE_SELECTED, CUSTOM_DATE_OPTION, FISCAL_FILTERS, NON_FISCAL_FILTERS } from '../constants/FiltersConstants';
import { HUBSPOT_DEFINED_FORECAST_TYPE_ID } from '../constants/ForecastTypeConstants';
import { CUSTOM_DATE_FORMAT } from '../constants/DateConstants';
import { SELECTED_TEAM_ID_STORAGE_KEY } from './localStorageKeys';
import { CRMSearchSortValue } from '../constants/SortValues';
import { DEAL_PROPERTIES } from '../constants/CrmSearchConstants';
import { ANALYZE_ROUTE } from '../constants/Tabs';
import { ALL_PIPELINES } from '../constants/Pipelines';
import { setValueInLocalStorage } from './localStorageHelper';
import { FORECAST_TYPE_KEY } from './localStorageKeys';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { getTimePeriodOffset, getTimePeriodOption, getFormattedDateRange, isTimePeriodCustom } from './dateUtils';
import { createInFilter, createEqualFilter, createBetweenFilter, createRollingDateRangeFilter, getTimeUnitForTimePeriod } from './filterOperators';

// for a given team id, return an array of all nested team ids
export const getAllNestedTeamIds = (teams, teamId) => {
  const team = teams.get(teamId);
  if (!team) return [];
  const childTeams = team.get('childTeams').toArray();
  return [...childTeams.reduce((allChildTeamIds, childTeamId) => [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  ...allChildTeamIds,
  // CRM index pages only filter team properly if passed as a string value
  // (https://hubspot.slack.com/archives/C80DDL2N7/p1601404440026400)
  childTeamId.toString(), ...getAllNestedTeamIds(teams, childTeamId)], [])];
};
export const makeDealSearchTeamFilter = teamIds => createInFilter(FILTERS_PROPERTY_NAME[FILTERS.TEAM], teamIds);
export const getFiltersFromQueryParams = queryParams => {
  const pipelineFilter = queryParams.getAll(FILTERS.PIPELINE);
  const timePeriodFilter = queryParams.get(FILTERS.TIME_PERIOD);
  const startDateFilter = queryParams.get('startDate');
  const endDateFilter = queryParams.get('endDate');
  const dealGroupingTypeFilter = queryParams.get(FILTERS.DEAL_GROUPING_TYPE);
  const dealGroupingFilter = queryParams.getAll(FILTERS.DEAL_GROUPING); // cleanup in #2401
  const dealStageGroupingFilter = queryParams.getAll(FILTERS.DEAL_STAGE_GROUPINGS);
  const forecastCategoryGroupingFilter = queryParams.getAll(FILTERS.FORECAST_CATEGORY_GROUPINGS);
  const forecastTypeFilter = Number(queryParams.get(FILTERS.FORECAST_TYPE));
  let queryParamsFilters = buildTypedMap({
    [FILTERS.FORECAST_TYPE]: typeof forecastTypeFilter === 'number' ? forecastTypeFilter : null,
    [FILTERS.PIPELINE]: pipelineFilter.length ? List(pipelineFilter) : null,
    [FILTERS.TIME_PERIOD]: timePeriodFilter,
    [FILTERS.DEAL_GROUPING_TYPE]: dealGroupingTypeFilter,
    // cleanup in #2401
    [FILTERS.DEAL_GROUPING]: dealGroupingFilter.length ? List(dealGroupingFilter) : null,
    [FILTERS.DEAL_STAGE_GROUPINGS]: dealStageGroupingFilter.length ? List(dealStageGroupingFilter) : null,
    [FILTERS.FORECAST_CATEGORY_GROUPINGS]: forecastCategoryGroupingFilter.length ? List(forecastCategoryGroupingFilter) : null
  });
  if (startDateFilter && endDateFilter) {
    queryParamsFilters = queryParamsFilters.set(FILTERS.TIME_PERIOD, ImmutableMap({
      startDate: startDateFilter,
      endDate: endDateFilter
    }));
  }
  return queryParamsFilters;
};
export const setQueryParams = ({
  history,
  filters,
  setDealGroupingParams = false
}) => {
  const queryParams = new URLSearchParams();
  filters.forEach((value, key) => {
    // only add deal grouping to query params if we're on the detail view
    if ((key === FILTERS.DEAL_STAGE_GROUPINGS || key === FILTERS.FORECAST_CATEGORY_GROUPINGS ||
    // cleanup in #2401
    key === FILTERS.DEAL_GROUPING) && !setDealGroupingParams) {
      return;
    }

    // don't add team to query params
    if (key === FILTERS.TEAM) {
      return;
    }
    if (List.isList(value)) {
      value.forEach(item => {
        queryParams.append(key, item);
      });
    } else {
      queryParams.set(key, value);
    }

    // handle custom vs. preset time period values
    if (key === FILTERS.TIME_PERIOD) {
      if (isTimePeriodCustom(value)) {
        value.forEach((v, k) => {
          queryParams.set(k, v);
          queryParams.delete(FILTERS.TIME_PERIOD);
        });
      } else {
        queryParams.delete('startDate');
        queryParams.delete('endDate');
      }
    }
  });
  const search = queryParams.toString();
  history.replace({
    search
  });

  // return search value for testing purposes
  return search;
};
const getValidForecastType = (allForecastTypes, forecastTypeId) => {
  var _allForecastTypes$fin;
  const selectedForecastType = allForecastTypes.find(forecast => forecast.forecastTypeId === forecastTypeId);
  if ((selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.amountOrDatePropertyRestrictedForUser) === false) {
    return forecastTypeId;
  }
  const defaultForecastType = allForecastTypes.find(forecast => forecast.defaultType);
  if ((defaultForecastType === null || defaultForecastType === void 0 ? void 0 : defaultForecastType.amountOrDatePropertyRestrictedForUser) === false) {
    return defaultForecastType.forecastTypeId;
  }
  return ((_allForecastTypes$fin = allForecastTypes.find(forecast => forecast.amountOrDatePropertyRestrictedForUser === false)) === null || _allForecastTypes$fin === void 0 ? void 0 : _allForecastTypes$fin.forecastTypeId) || null;
};
export const sanitizeInitialFilters = ({
  filters,
  allPipelines,
  allForecastTypes,
  fiscalOffset
}) => {
  var _filters$get, _filters$get2, _filters$get3, _filters$get4, _filters$get5, _filters$get6, _filters$get7;
  // returns a sanitized version of the filter state where:
  // pipelines must be valid pipelines
  // time period must be a valid time period (one of the presets)
  // start date and end date must be valid dates in the correct format
  // deal grouping type must be a valid deal grouping type

  // TODO: add support to ensure deal groupings are all valid

  const isAnalyze = window.location.pathname.includes(ANALYZE_ROUTE);
  const pipelines = filters === null || filters === void 0 || (_filters$get = filters.get) === null || _filters$get === void 0 ? void 0 : _filters$get.call(filters, FILTERS.PIPELINE);
  const timePeriod = filters === null || filters === void 0 || (_filters$get2 = filters.get) === null || _filters$get2 === void 0 ? void 0 : _filters$get2.call(filters, FILTERS.TIME_PERIOD);
  const dealGroupingType = (_filters$get3 = filters.get) === null || _filters$get3 === void 0 ? void 0 : _filters$get3.call(filters, FILTERS.DEAL_GROUPING_TYPE);
  const dealGrouping = (_filters$get4 = filters.get) === null || _filters$get4 === void 0 ? void 0 : _filters$get4.call(filters, FILTERS.DEAL_GROUPING); // cleanup in #2401
  const dealStageGrouping = (_filters$get5 = filters.get) === null || _filters$get5 === void 0 ? void 0 : _filters$get5.call(filters, FILTERS.DEAL_STAGE_GROUPINGS);
  const forecastCategoryGrouping = (_filters$get6 = filters.get) === null || _filters$get6 === void 0 ? void 0 : _filters$get6.call(filters, FILTERS.FORECAST_CATEGORY_GROUPINGS);
  const forecastType = (_filters$get7 = filters.get) === null || _filters$get7 === void 0 ? void 0 : _filters$get7.call(filters, FILTERS.FORECAST_TYPE);
  let sanitizedState = buildTypedMap({});

  // Sanitize forecast type
  if (forecastType !== null && forecastType !== undefined) {
    const validForecastType = getValidForecastType(allForecastTypes, forecastType);
    if (validForecastType !== null) {
      sanitizedState = sanitizedState.set(FILTERS.FORECAST_TYPE, validForecastType);
    } else {
      // Even though no forecast type is valid, we'll set the default since the filter needs a value
      sanitizedState = sanitizedState.set(FILTERS.FORECAST_TYPE, HUBSPOT_DEFINED_FORECAST_TYPE_ID);
    }
  }

  // Sanitize pipelines
  if (pipelines && allPipelines.size) {
    if (List.isList(pipelines)) {
      // is analyze check can be removed in #2284
      const allValidPipelines = isAnalyze ? allPipelines.push(buildTypedMap({
        pipelineId: ALL_PIPELINES
      })) : allPipelines;
      const validSelectedPipelines = pipelines.filter(pipeline => {
        // Check if pipeline is valid
        return !!allValidPipelines.find(value => value.get('pipelineId') === pipeline);
      });
      if (validSelectedPipelines.size) {
        sanitizedState = sanitizedState.set(FILTERS.PIPELINE, List(dealGroupingType === DEAL_STAGE || isAnalyze ? [validSelectedPipelines.first()] : validSelectedPipelines));
      } else {
        var _allPipelines$first;
        sanitizedState = sanitizedState.set(FILTERS.PIPELINE, List([(_allPipelines$first = allPipelines.first()) === null || _allPipelines$first === void 0 ? void 0 : _allPipelines$first.get('pipelineId')]));
      }
    }
  } else if (allPipelines.size) {
    var _allPipelines$first2;
    // If no pipelines exist in the filters, then select the first pipeline of valid pipelines
    sanitizedState = sanitizedState.set(FILTERS.PIPELINE, List([(_allPipelines$first2 = allPipelines.first()) === null || _allPipelines$first2 === void 0 ? void 0 : _allPipelines$first2.get('pipelineId')]));
  } else {
    logRavenException({}, 'No pipelines exist to be set for pipeline filter');
  }

  // Sanitize time period
  if (timePeriod) {
    if (isTimePeriodCustom(timePeriod)) {
      const startDate = timePeriod.get('startDate');
      const endDate = timePeriod.get('endDate');
      if (moment(startDate, CUSTOM_DATE_FORMAT, true).isValid() && moment(endDate, CUSTOM_DATE_FORMAT, true).isValid() && moment(endDate, CUSTOM_DATE_FORMAT).isAfter(moment(startDate, CUSTOM_DATE_FORMAT))) {
        sanitizedState = sanitizedState.set(FILTERS.TIME_PERIOD, buildTypedMap({
          startDate,
          endDate
        }));
      }
    } else if (TIME_PERIOD_FILTERS[timePeriod]) {
      if (fiscalOffset !== 0 && !NON_FISCAL_FILTERS.includes(timePeriod)) {
        sanitizedState = sanitizedState.set(FILTERS.TIME_PERIOD, timePeriod);
      }
      if (fiscalOffset === 0 && !FISCAL_FILTERS.includes(timePeriod)) {
        sanitizedState = sanitizedState.set(FILTERS.TIME_PERIOD, timePeriod);
      }
    }
  }
  if (dealGroupingType) {
    if (dealGroupingType === DEAL_STAGE || dealGroupingType === FORECAST_CATEGORY) {
      sanitizedState = sanitizedState.set(FILTERS.DEAL_GROUPING_TYPE, dealGroupingType);
    }
  }
  if (dealStageGrouping) {
    sanitizedState = sanitizedState.set(FILTERS.DEAL_STAGE_GROUPINGS, dealStageGrouping);
  }
  if (forecastCategoryGrouping) {
    sanitizedState = sanitizedState.set(FILTERS.FORECAST_CATEGORY_GROUPINGS, forecastCategoryGrouping);
  }

  // This is to handle the old deal grouping filter
  if (!dealStageGrouping && !forecastCategoryGrouping && dealGrouping) {
    // this should get sorted and validated, but getting reference resolvers at this level is a bit tricky
    // these will be filtered/validated to the correct values in DealTableFilters/dealGroupingUtils
    sanitizedState = sanitizedState.set(FILTERS.FORECAST_CATEGORY_GROUPINGS, dealGrouping);
    sanitizedState = sanitizedState.set(FILTERS.DEAL_STAGE_GROUPINGS, dealGrouping);
  }

  // LOCAL DEVELOPMENT ONLY: preview FullStory session recordings with:
  // sanitizedState = sanitizedState.set('_fs_preview', true);

  return sanitizedState;
};
export const makeTimePeriodFilterOptions = memoize((currentDate, offset) => {
  const filters = Object.values(Object.assign({}, TIME_PERIOD_FILTERS, TIME_PERIOD_FILTER_CUSTOM)).filter(timePeriodFilter => offset ? !NON_FISCAL_FILTERS.includes(timePeriodFilter) : !FISCAL_FILTERS.includes(timePeriodFilter));
  return filters.map(filter => {
    return {
      help: filter === CUSTOM_DATE_OPTION ? I18n.text('repOverviewTable.filters.timePeriod.custom.helpText') : getFormattedDateRange({
        currentDate,
        timePeriod: filter,
        offset
      }),
      text: I18n.text(TIME_PERIOD_FILTERS_TO_OPTION_TEXT_STRINGS[filter]),
      value: filter
    };
  });
});
export const makeCrmSearchDateRangeFilterQuery = (property, timePeriodFilterState, fiscalYearOffset) => {
  if (!timePeriodFilterState) {
    return null;
  }
  if (isTimePeriodCustom(timePeriodFilterState)) {
    const startDate = timePeriodFilterState.get('startDate');
    const endDate = timePeriodFilterState.get('endDate');
    if (startDate && endDate) {
      return createBetweenFilter(property, startDate, endDate);
    }
    return null;
  }

  // At this point, timePeriodFilterState is a rolling (non-custom) time period
  const timeUnit = getTimeUnitForTimePeriod(timePeriodFilterState);
  const isFiscalPeriod = timePeriodFilterState.includes('FISCAL');
  const isNextPeriod = timePeriodFilterState.startsWith('NEXT_');
  const isThisPeriod = timePeriodFilterState.startsWith('THIS_');
  return createRollingDateRangeFilter(property, timeUnit, 1, isThisPeriod,
  // Only include current period for THIS_* periods
  isNextPeriod,
  // Only roll forward for NEXT_* periods
  isFiscalPeriod ? fiscalYearOffset : undefined);
};
export const makeDealSearchOwnerFilter = ownerId => createInFilter(FILTERS_PROPERTY_NAME[FILTERS.OWNERS],
// CRM index pages only filter owner properly if passed as a string value (https://hubspot.slack.com/archives/C80DDL2N7/p1601404440026400)
ownerId ? [ownerId.toString()] : []);
export const makePipelineFilterQuery = (pipelineFilterState, propertyName = FILTERS_PROPERTY_NAME[FILTERS.PIPELINE]) => {
  const pipelineIds = pipelineFilterState.size ? pipelineFilterState.toArray() : [NO_VALUE_SELECTED];
  return createInFilter(propertyName, pipelineIds);
};
const makeOpenDealsFilterQuery = () => createEqualFilter(FILTERS_PROPERTY_NAME[FILTERS.IS_DEAL_CLOSED], false);
export const makeDealGroupingFilterQuery = (dealGroupingFilterState, dealGroupingType) => {
  if (!(dealGroupingFilterState !== null && dealGroupingFilterState !== void 0 && dealGroupingFilterState.size)) return null;
  return createInFilter(DEAL_GROUPING_TYPE[dealGroupingType].propertyName, dealGroupingFilterState.toArray());
};
export const buildFilterGroup = filterQueries => {
  return {
    filters: filterQueries
  };
};
export const makeDealFilterGroupByOwnerQuery = ({
  id
}) => {
  const idQueries = [makeOpenDealsFilterQuery(), makeDealSearchOwnerFilter(id)];
  return [buildFilterGroup(idQueries)];
};
export const makeDealFilterGroupQuery = ({
  id,
  filterState,
  dealGroupingType,
  isTeam,
  teams,
  fiscalYearOffset
}) => {
  const timePeriodFilterState = filterState.get(FILTERS.TIME_PERIOD);
  const pipelineFilterState = filterState.get(FILTERS.PIPELINE);
  const dealGroupingFilterState = filterState.get(dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS);
  const baseFilterQueries = [makePipelineFilterQuery(pipelineFilterState)];
  const dateRangeFilter = makeCrmSearchDateRangeFilterQuery(DEAL_PROPERTIES.CLOSE_DATE, timePeriodFilterState, fiscalYearOffset);
  if (dateRangeFilter) {
    baseFilterQueries.push(dateRangeFilter);
  }
  const dealGroupingFilter = makeDealGroupingFilterQuery(dealGroupingFilterState, dealGroupingType);
  if (dealGroupingFilter) {
    baseFilterQueries.push(dealGroupingFilter);
  }
  let idQueries = baseFilterQueries;
  if (isTeam && !!id) {
    idQueries = [...baseFilterQueries, makeDealSearchTeamFilter([id === null || id === void 0 ? void 0 : id.toString(), ...getAllNestedTeamIds(teams, id)])];
  } else if (id) {
    idQueries = [...baseFilterQueries, makeDealSearchOwnerFilter(id)];
  }
  return [buildFilterGroup(idQueries)];
};
export const filterStateToEventProperties = filterState => pick(mapKeys(filterState.toJS(), (_, k) => camelCase(k)), ['timePeriod', 'team', 'pipeline', 'forecastCategory']);
export const getFilterLocalStorageValue = () => {
  try {
    const savedFilters = SafeStorage.getItem(FILTER_STORAGE_KEY);
    const portalId = PortalIdParser.get();
    if (!savedFilters) {
      return null;
    }
    // TODO: Stop converting filters to immutable map
    const parsedFilters = fromJS(JSON.parse(savedFilters));
    const parsedFiltersForPortal = parsedFilters.get(portalId === null || portalId === void 0 ? void 0 : portalId.toString());
    const savedForecastTypes = SafeStorage.getItem(FORECAST_TYPE_KEY);
    const parsedForecastTypes = JSON.parse(savedForecastTypes);
    const parsedForecastTypeForPortal = portalId && parsedForecastTypes && parsedForecastTypes[portalId];
    const pipelines = parsedFiltersForPortal && parsedFiltersForPortal.get('pipeline');

    // Some users have reached a bad state where their local storage pipeline value
    // has multiple pipelines but their local storage forecast type is deal stage
    if (pipelines.size > 1 && parsedForecastTypeForPortal === DEAL_STAGE) {
      setValueInLocalStorage(FILTER_STORAGE_KEY, ImmutableMap());
      return null;
    }
    const timePeriod = parsedFiltersForPortal && parsedFiltersForPortal.get('time_period');
    if (!isTimePeriodCustom(timePeriod)) {
      return parsedFilters;
    }
    const startDate = timePeriod.get('startDate');
    const endDate = timePeriod.get('endDate');

    // Some users have reached a bad state where their local storage has startDate > endDate
    if (!startDate || !endDate || startDate > endDate) {
      setValueInLocalStorage(FILTER_STORAGE_KEY, ImmutableMap());
      return null;
    }
    return parsedFilters;
  } catch (e) {
    return null;
  }
};
export const getValueFromLocalStorage = key => {
  try {
    const dataObject = SafeStorage.getItem(key);
    const portalId = PortalIdParser.get();
    if (!dataObject || !portalId) {
      return null;
    }
    return JSON.parse(dataObject)[portalId];
  } catch (e) {
    return null;
  }
};
export const getLastSavedTeam = () => getValueFromLocalStorage(SELECTED_TEAM_ID_STORAGE_KEY);
export const saveFiltersToLocalStorage = state => {
  const portalId = PortalIdParser.get();
  const savedFilters = getFilterLocalStorageValue() || ImmutableMap();
  const allFilters = savedFilters.set(portalId === null || portalId === void 0 ? void 0 : portalId.toString(), state);
  try {
    SafeStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(allFilters.toJS()));
  } catch (e) {
    return;
  }
};
export const getFiltersFromLocalStorage = () => {
  const portalId = PortalIdParser.get();
  const savedFilters = getFilterLocalStorageValue() || ImmutableMap();
  const filtersForPortal = savedFilters.get(portalId === null || portalId === void 0 ? void 0 : portalId.toString());

  // For bug arising from pipeline filter being an empty array (FORECAST-487)
  // If portal is in a bad state where pipeline filter is an empty array, reset local storage
  if (filtersForPortal && !filtersForPortal.get(FILTERS.PIPELINE).size) {
    return ImmutableMap();
  }

  // For reverse compatibility: if users have a number saved in localstorage for pipelines vs a list, we convert to list
  const selectedPipelines = filtersForPortal ? filtersForPortal.get(FILTERS.PIPELINE) : null;
  if (selectedPipelines && !List.isList(selectedPipelines)) {
    return filtersForPortal.set(FILTERS.PIPELINE, List([selectedPipelines]));
  }
  return filtersForPortal;
};
export const makeForecastSearchQuery = ({
  dealGroupingType,
  filters,
  isCustomDate,
  isTeam,
  options,
  teamOrOwnerId,
  timePeriod,
  totalDealAmountOption,
  forecastTypeId
}) => {
  var _filters$get8;
  let startDate;
  let endDate;
  let timePeriodOptions;
  const idKey = isTeam ? 'teamId' : 'ownerId';
  if (isCustomDate) {
    startDate = timePeriod.get('startDate');
    endDate = timePeriod.get('endDate');
    timePeriodOptions = {
      timePeriodOption: TIME_PERIOD_FILTER_CUSTOM.CUSTOM,
      timePeriodOffset: 0,
      customEndDate: endDate,
      customStartDate: startDate
    };
  } else {
    timePeriodOptions = {
      timePeriodOption: getTimePeriodOption(timePeriod),
      timePeriodOffset: getTimePeriodOffset(timePeriod)
    };
  }
  return Object.assign({
    count: options.pageSize,
    offset: options.page * options.pageSize,
    pipelines: filters && filters.get('pipeline').toArray(),
    timePeriod: timePeriodOptions,
    [idKey]: teamOrOwnerId,
    [DEAL_GROUPING_TYPE[dealGroupingType].dealQueryKey]: (_filters$get8 = filters.get) === null || _filters$get8 === void 0 || (_filters$get8 = _filters$get8.call(filters, dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS)) === null || _filters$get8 === void 0 ? void 0 : _filters$get8.toArray(),
    sorts: [...options.sorts, {
      property: 'dealId',
      order: CRMSearchSortValue.DESCENDING
    }],
    totalDealAmountOption
  }, forecastTypeId ? {
    forecastTypeId
  } : {});
};
export const isFilteredToMultiButNotAllPipelines = (pipelines, value) => {
  const valueLength = Array.isArray(value) ? value.length : value.size;
  return !(pipelines.size === valueLength) && valueLength > 1;
};